import { getMenuServices } from '../../utils/menuServices';
import { getServiceIcon } from "../../utils/serviceIcons";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  LayoutDashboard,
  X } from
"lucide-react";
import { cn } from "../../lib/utils";
import { buildNavTree, cleanName, isPathActive } from "../../router/navigation";
import { getServiceMeta } from "../../utils/serviceColors";
import { useTranslation } from "react-i18next";

function isLegacyDashboardLink(to) {
  return typeof to === "string" && to.startsWith("/dashboard.html");
}

/**
 * Desktop services open on hover, with modules and submodules in adjacent
 * panels. The header toggle opens an accordion drawer on every screen size.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [menuServices, setMenuServices] = useState(getMenuServices);
  useEffect(() => {
    const refresh = () => setMenuServices(getMenuServices());
    window.addEventListener('session-updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('session-updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);
  const navTree = useMemo(() => buildNavTree(menuServices), [menuServices]);

  // Desktop icon-only collapse state (persisted)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("hrms_sidebar_collapsed") === "true";
    }
    return false;
  });

  // Overlay drawer state for desktop and mobile
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // The drawer always shows full labels.
  const collapsed = isPanelCollapsed && !isDrawerOpen;
  const asideRef = useRef(null);
  const flyoutRef = useRef(null);
  const [flyoutService, setFlyoutService] = useState(null);
  const [flyoutModule, setFlyoutModule] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState(null);
  const closeTimer = useRef(null);
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      setFlyoutService(null);
      setFlyoutModule(null);
      setFlyoutPosition(null);
    }, 180);
  };

  useEffect(() => {
    const dismiss = () => {
      window.clearTimeout(closeTimer.current);
      setFlyoutService(null);
      setFlyoutModule(null);
      setFlyoutPosition(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") { dismiss(); setIsDrawerOpen(false); }
    };
    window.addEventListener("resize", dismiss);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(closeTimer.current);
      window.removeEventListener("resize", dismiss);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("hrms_sidebar_collapsed", String(next));
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      return next;
    });
  }, []);

  // The header toggle opens the same drawer on every screen size.
  useEffect(() => {
    const handleToggleSidebar = () => setIsDrawerOpen((prev) => !prev);
    window.addEventListener("toggle-sidebar", handleToggleSidebar);
    return () => window.removeEventListener("toggle-sidebar", handleToggleSidebar);
  }, []);

  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    navTree.forEach((svc) => svc.modules.forEach((mod) => {
      if (mod.submodules.some((sub) => isPathActive(pathname, sub.to))) {
        initial[svc.name] = true;
        initial[svc.name + "::" + mod.name] = true;
      }
    }));
    return initial;
  });
  const toggleService = (name) => {
    setExpanded((prev) => prev[name] ? {} : { [name]: true });
  };
  const toggleModule = (service, name) => {
    const key = service + "::" + name;
    setExpanded((prev) => ({ [service]: true, [key]: !prev[key] }));
  };
  const closeNavigation = () => {
    closeFlyout();
    setIsDrawerOpen(false);
  };

  // Close the drawer and flyouts after navigating
  useEffect(() => {
    setIsDrawerOpen(false);
    setFlyoutService(null);
    setFlyoutModule(null);
    setFlyoutPosition(null);
  }, [pathname]);

  useEffect(() => {
    window.clearTimeout(closeTimer.current);
    setFlyoutService(null);
    setFlyoutModule(null);
    setFlyoutPosition(null);
  }, [collapsed, isDrawerOpen]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (
        !flyoutRef.current?.contains(event.target) &&
        !asideRef.current?.contains(event.target)
      ) {
        setFlyoutService(null);
        setFlyoutModule(null);
        setFlyoutPosition(null);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const closeFlyout = () => {
    cancelClose();
    setFlyoutService(null);
    setFlyoutModule(null);
    setFlyoutPosition(null);
  };

  const openServiceFlyout = (svcName, anchorEl) => {
    cancelClose();
    if (flyoutService === svcName) return;
    const rect = anchorEl?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 272;
      const gap = 8;
      const preferredMax = Math.min(window.innerHeight * 0.7, 512);
      let left = (asideRef.current?.getBoundingClientRect().right ?? rect.right) + gap;
      if (left + menuWidth > window.innerWidth - gap) {
        left = Math.max(gap, rect.left - menuWidth - gap);
      }

      const spaceBelow = window.innerHeight - rect.top - gap;
      const spaceAbove = rect.bottom - gap;
      // Stay level with the icon. If the icon is too low for the menu to
      // open downward, grow upward from the icon instead of jumping to the
      // top of the viewport.
      const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;
      setFlyoutPosition(
        openUpward
          ? {
              left,
              bottom: window.innerHeight - rect.bottom,
              maxHeight: Math.min(preferredMax, Math.max(spaceAbove, 120)),
            }
          : {
              left,
              top: rect.top,
              maxHeight: Math.min(preferredMax, Math.max(spaceBelow, 120)),
            }
      );
    }
    setFlyoutService(svcName);
    setFlyoutModule(null);
  };

  const openModuleFlyout = (event, module) => {
    cancelClose();
    if (!module.submodules.length) {
      setFlyoutModule(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const panel = flyoutRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.top - 8;
    const spaceAbove = rect.bottom - 8;
    const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;
    setSubmenuPosition({
      left: panel.right + 264 > window.innerWidth ? Math.max(8, panel.left - 256) : panel.right,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.bottom, maxHeight: Math.min(512, spaceAbove) }
        : { top: rect.top, maxHeight: Math.min(512, spaceBelow) })
    });
    setFlyoutModule(module.name);
  };

  const navItemClass = (active) =>
  cn(
    "w-full flex items-center justify-between rounded-xl text-sm font-medium transition-all text-left cursor-pointer",
    isDrawerOpen && "text-xs",
    collapsed ? "px-1 py-2.5 justify-center" : "px-3.5 py-2.5",
    active ?
    "bg-secondary text-foreground" :
    "text-muted-foreground hover:bg-secondary hover:text-foreground"
  );

  /**
   * Services expand inside the drawer and use flyouts in the desktop panel.
   */
  const renderNavTree = () =>
  navTree.map((svc) => {
    const meta = getServiceMeta(svc.name);
    const ServiceIcon = getServiceIcon(svc.name);
    const flyoutOpen = !isDrawerOpen && flyoutService === svc.name;
    const serviceActive = svc.modules.some((mod) =>
      mod.submodules.some((sub) => isPathActive(pathname, sub.to))
    );

    return (
      <div key={svc.name} className="space-y-1">
          {/* Level 1: Service — the only level that carries an icon, mapped
             to something meaningful per service (see SERVICE_ICONS above). */}
          <button
            type="button"
            onMouseEnter={(event) => {
              if (!isDrawerOpen) openServiceFlyout(svc.name, event.currentTarget);
            }}
            onMouseLeave={() => { if (!isDrawerOpen) scheduleClose(); }}
            onFocus={(event) => {
              if (!isDrawerOpen) openServiceFlyout(svc.name, event.currentTarget);
            }}
            onClick={(event) => isDrawerOpen ? toggleService(svc.name) : openServiceFlyout(svc.name, event.currentTarget)}
            className={cn(
              "w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold text-foreground hover:bg-secondary transition text-left cursor-pointer",
              isDrawerOpen && "text-xs",
              collapsed && "justify-center px-1",
              (flyoutOpen || serviceActive) && "bg-secondary"
            )}
            title={svc.name}
            aria-expanded={isDrawerOpen ? !!expanded[svc.name] : flyoutOpen}>
          
              <div className="flex items-center gap-3">
                <ServiceIcon className="h-[18px] w-[18px] shrink-0" style={{ color: meta.color }} />
                {!collapsed && <span>{svc.name}</span>}
              </div>
              {!collapsed && (isDrawerOpen && expanded[svc.name] ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />)}
            </button>
            {isDrawerOpen && expanded[svc.name] && (
              <div className="ml-3 border-l border-border pl-3">
                {svc.modules.map((mod) => (
                  <div key={mod.name}>
                    <button type="button" onClick={() => toggleModule(svc.name, mod.name)}
                      aria-expanded={!!expanded[svc.name + "::" + mod.name]}
                      className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold hover:bg-secondary">
                      <span>{cleanName(mod.name)}</span>
                      {expanded[svc.name + "::" + mod.name] ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                    </button>
                    {expanded[svc.name + "::" + mod.name] && (
                      <div className="ml-3 border-l border-border pl-2">
                        {mod.submodules.map((sub) => {
                          const className = cn("block rounded-lg px-3 py-2 text-xs", isPathActive(pathname, sub.to) ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground");
                          return isLegacyDashboardLink(sub.to)
                            ? <a key={sub.name} href={sub.to} className={className} onClick={closeNavigation}>{cleanName(sub.name)}</a>
                            : <Link key={sub.name} to={sub.to} className={className} onClick={closeNavigation}>{cleanName(sub.name)}</Link>;
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>);

  });

  const flyoutSvc = !isDrawerOpen && flyoutService
    ? navTree.find((svc) => svc.name === flyoutService)
    : null;
  const flyoutMod = flyoutSvc && flyoutModule
    ? flyoutSvc.modules.find((mod) => mod.name === flyoutModule)
    : null;

  return (
    <>
      {/* Drawer backdrop on desktop and mobile */}
      {isDrawerOpen &&
      <div
        onClick={() => setIsDrawerOpen(false)}
        className="fixed inset-x-0 bottom-0 top-[112px] z-40 bg-[#0a1230]/50 backdrop-blur-xs sm:top-[112px]" />

      }

      <aside
        ref={asideRef}
        className={cn(
          "hrms-shell__sidebar flex-col shrink-0 text-foreground px-4 pb-4 pt-0",
          // Overlay drawer on every screen size
          isDrawerOpen ? "hrms-shell__sidebar--open flex fixed bottom-0 left-0 top-[112px] z-50 sm:top-[112px]" : "hidden",
          // Desktop panel when the drawer is closed
          !isDrawerOpen && "md:flex md:static md:inset-auto md:z-auto md:transition-all md:duration-300"
        )}
        style={{ width: collapsed ? "76px" : "260px" }}>
        
        <div className="flex flex-col h-full min-h-0">
          {isDrawerOpen &&
          <div className="mb-2 flex h-12 shrink-0 items-center justify-end border-b border-border px-1">
            <button
              type="button"
              onClick={() => setIsDrawerOpen(false)}
              className="h-8 w-8 shrink-0 rounded-lg border border-border grid place-items-center text-muted-foreground hover:text-foreground"
              aria-label={t("common.closeMenu")}>
              
                <X className="h-4 w-4" />
              </button>
          </div>
          }
          <nav onScroll={closeFlyout} className="space-y-4 flex-1 overflow-y-auto pr-1 pt-2">
            <Link to="/" onClick={closeNavigation} className={navItemClass(pathname === "/")}>
              <div className="flex items-center gap-3">
                <LayoutDashboard className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span>{t("common.dashboard")}</span>}
              </div>
            </Link>

            {renderNavTree()}

            {navTree.length === 0 && !collapsed &&
            <p className="px-3.5 text-xs text-muted-foreground">
                {t("common.noServices")}
              </p>
            }
          </nav>

          {/* User profile & Sign Out block
            <div className={cn("pt-4 mt-4 border-t border-border flex gap-2 min-w-0", collapsed ? "flex-col items-center" : "flex-row items-center justify-between")}>
             <div className={cn("flex items-center gap-3 px-3.5 py-2 rounded-xl bg-white/5 border border-white/5 min-w-0", collapsed ? "justify-center px-1" : "flex-1")}>
               <div className="h-9 w-9 rounded-full bg-primary border border-white/20 flex items-center justify-center font-bold text-white text-xs shrink-0" title={collapsed ? `${emp.first_name || ""} ${emp.last_name || ""}` : undefined}>
                 {initials}
               </div>
               {!collapsed && (
                 <div className="flex flex-col min-w-0">
                   <span className="text-sm font-semibold text-white truncate">
                     {emp.first_name || ""} {emp.last_name || ""}
                   </span>
                   <span className="text-[10px] text-muted-foreground truncate">
                     {emp.designation_name || "Employee"}
                   </span>
                 </div>
               )}
             </div>
              <button
               type="button"
               onClick={handleLogout}
               className={cn(
                 "rounded-xl flex items-center justify-center text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition cursor-pointer bg-white/5 border border-white/5 shrink-0",
                 collapsed ? "h-9 w-9 border-0 bg-transparent hover:bg-white/5" : "h-11 w-11"
               )}
               title="Sign Out"
             >
               <LogOut className="h-[18px] w-[18px] shrink-0" />
             </button>
            </div> */
          }

          {/* Collapse toggle (both desktop and mobile) */}
          <div className="pt-4 mt-4 border-t border-border">
            <button
              type="button"
              onClick={isDrawerOpen ? () => setIsDrawerOpen(false) : togglePanel}
              className={cn(
                "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground transition cursor-pointer",
                isDrawerOpen && "text-xs",
                collapsed && "justify-center px-1"
              )}>
              
              <ChevronLeft
                className={cn(
                  "h-[18px] w-[18px] transition-transform shrink-0",
                  (collapsed || isDrawerOpen) && "rotate-0",
                  collapsed && !isDrawerOpen && "rotate-180"
                )} />
              
              {(!collapsed || isDrawerOpen) && <span>{t("common.collapsePanel")}</span>}
            </button>
          </div>
        </div>
      </aside>

      {flyoutSvc && flyoutPosition && createPortal(
        <div
          ref={flyoutRef}
          onMouseEnter={cancelClose}
          onMouseLeave={() => { if (!isDrawerOpen) scheduleClose(); }}
          className="fixed z-60 w-[272px]"
          style={flyoutPosition}>
          <div
            className="overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
            style={{ maxHeight: flyoutPosition.maxHeight }}
            onScroll={() => setFlyoutModule(null)}>
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {flyoutSvc.name}
            </p>
            <div className="divide-y divide-border">
              {flyoutSvc.modules.map((module) => (
                <button
                  key={module.name}
                  type="button"
                  onMouseEnter={(event) => { if (!isDrawerOpen) openModuleFlyout(event, module); }}
                  onFocus={(event) => { if (!isDrawerOpen) openModuleFlyout(event, module); }}
                  onClick={(event) => openModuleFlyout(event, module)}
                  aria-expanded={flyoutModule === module.name}
                  className={cn(
                    "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100",
                    flyoutModule === module.name && "bg-slate-100"
                  )}>
                  {cleanName(module.name)}
                  {module.submodules.length > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />}
                </button>
              ))}
            </div>
          </div>
          {flyoutMod && submenuPosition && (
            <div
              className="fixed w-64 divide-y divide-border overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
              style={submenuPosition}>
              {flyoutMod.submodules.map((submodule) => {
                const active = isPathActive(pathname, submodule.to);
                const className = cn(
                  "block px-3 py-2 text-sm transition",
                  active ? "bg-accent text-white" : "text-slate-700 hover:bg-slate-100"
                );
                return isLegacyDashboardLink(submodule.to) ? (
                  <a key={submodule.name} href={submodule.to} className={className} onClick={() => { closeFlyout(); setIsDrawerOpen(false); }}>
                    {cleanName(submodule.name)}
                  </a>
                ) : (
                  <Link key={submodule.name} to={submodule.to} className={className} onClick={() => { closeFlyout(); setIsDrawerOpen(false); }}>
                    {cleanName(submodule.name)}
                  </Link>
                );
              })}
            </div>
          )}
        </div>,
        document.body)}
    </>);

}
